import { useMemo } from 'react'

import dayjs from 'dayjs'

import {
  AppointmentEvents,
  OrderDetailActiveAppointments,
  TimeSlotSchema,
  transformedAppointmentResponseSchema,
} from '@giffgaff/broadband-common'

import { AxiosError } from 'axios'
import { z } from 'zod'

import { TCustomAxiosErrorBody } from '@fe-toolkit/react-query-hooks-toolkit'

import { useFetchMoreAvailableAppointments } from '@/hooks/apiHooks'

import {
  HandleDateChange,
  HandleTimeSlotChange,
  PlanAppointmentLegacyNotes,
  TManagedVisitType,
} from '../../utils/propTypeHelpers'
import { AppointmentInfoUpdate, appointmentUpdate } from '../../ChangeInstallDates.container'

import ChangeAppointmentInfo from './ChangeAppointmentInfo'

export type ChangeAppointmentInfoContainerProps = {
  relevantActiveAppointment: OrderDetailActiveAppointments
  planAppointmentNotes?: PlanAppointmentLegacyNotes
  appointmentInfoUpdate: AppointmentInfoUpdate
  isFinalTab: boolean
  singleAppointment: boolean
  updatedAppointments: appointmentUpdate[]
  leadTimeClash: boolean
  // Callbacks from parent
  handleDateChange: HandleDateChange
  handleTimeSlotChange: HandleTimeSlotChange
  handleAppointmentInfoUpdate: (
    field: 'contactFirstName' | 'contactLastName' | 'contactPhoneNumber',
    value: string,
    destination: TManagedVisitType,
  ) => void
  setSelectedNotes: (field: string, value: string[]) => void
  handleRequestedCallChange: (value: boolean) => void
  handleCancelAppointmentChange: () => void
  handleMainButtonClick: (date: string | undefined, timeSlot: TimeSlotSchema | undefined) => void
  requestedAppointmentNoLongerAvailable: boolean
  rebookingRequiredBlocked?: boolean
  isUpdatingAppointments: boolean
  isUpdatingAppointmentsError: boolean

  // For lead time display
  groupedActiveAppointments: {
    PRE_WORK?: OrderDetailActiveAppointments
    IN_HOME?: OrderDetailActiveAppointments
  }
  appointments?: z.infer<typeof transformedAppointmentResponseSchema> // This is being passed down for the calendar to show available dates, but ideally the calendar should be able to work with the groupedActiveAppointments data instead to avoid prop drilling
  getAppointmentsError?: AxiosError<TCustomAxiosErrorBody> | null
  isFetching: boolean
  isLoading: boolean
}

const ChangeAppointmentInfoContainer = ({
  relevantActiveAppointment,
  planAppointmentNotes,
  appointmentInfoUpdate,
  isFinalTab,
  singleAppointment,
  updatedAppointments,
  leadTimeClash,
  handleDateChange,
  handleTimeSlotChange,
  handleAppointmentInfoUpdate,
  setSelectedNotes,
  handleRequestedCallChange,
  handleCancelAppointmentChange,
  handleMainButtonClick,
  requestedAppointmentNoLongerAvailable,
  rebookingRequiredBlocked,
  isUpdatingAppointments,
  isUpdatingAppointmentsError,
  groupedActiveAppointments,
  appointments,
  getAppointmentsError,
  isFetching,
  isLoading,
}: ChangeAppointmentInfoContainerProps) => {
  // Create appointment events
  const appointmentEvents: AppointmentEvents | undefined = useMemo(() => {
    if (!relevantActiveAppointment?.id) return undefined

    return {
      installDateSelectClick(data) {
        handleDateChange(
          relevantActiveAppointment.id,
          dayjs(data.date).format('YYYY-MM-DD'),
          data.slot,
          relevantActiveAppointment.visitType as TManagedVisitType,
        )
      },
      installTimeSelectClick(data) {
        handleTimeSlotChange(
          relevantActiveAppointment.id,
          data.slot.earliestArrivalTime,
          data.slot.latestArrivalTime,
          data.slot.formattedArrivalTime,
          data.slot.slot,
        )
      },
    }
  }, [relevantActiveAppointment.id, relevantActiveAppointment.visitType, handleDateChange, handleTimeSlotChange])

  return (
    <ChangeAppointmentInfo
      appointmentEvents={appointmentEvents}
      appointmentInfoUpdate={appointmentInfoUpdate}
      appointmentIsInThePast={dayjs(relevantActiveAppointment.appointmentDate).isBefore(dayjs(), 'day')} // this is where we need to use the phase not the date.
      appointments={appointments}
      getAppointmentsError={getAppointmentsError || null}
      groupedActiveAppointments={groupedActiveAppointments}
      handleAppointmentInfoUpdate={handleAppointmentInfoUpdate}
      handleCancelAppointmentChange={handleCancelAppointmentChange}
      handleMainButtonClick={handleMainButtonClick}
      handleRequestedCallChange={handleRequestedCallChange}
      isFetching={isFetching}
      isFinalTab={isFinalTab}
      isLoading={isLoading}
      isUpdatingAppointments={isUpdatingAppointments}
      isUpdatingAppointmentsError={isUpdatingAppointmentsError}
      leadTimeClash={leadTimeClash}
      planAppointmentNotes={planAppointmentNotes}
      rebookingRequiredBlocked={rebookingRequiredBlocked}
      relevantActiveAppointment={relevantActiveAppointment}
      requestedAppointmentNoLongerAvailable={requestedAppointmentNoLongerAvailable}
      setSelectedNotes={setSelectedNotes}
      singleAppointment={singleAppointment}
      updatedAppointments={updatedAppointments}
      useFetchMoreAvailableAppointments={useFetchMoreAvailableAppointments}
    />
  )
}

export default ChangeAppointmentInfoContainer
